/**
 * Finding references in judge reasoning — "(step-6, finding <uuid>)".
 * Known ids become finding segments carrying the finding's title (the
 * doubled noun "finding" before them is dropped, so the text reads
 * "“Title”"). Unknown ids are removed together with their "finding"
 * prefix and the separator that joined them to a neighbour, because a
 * raw UUID means nothing to a reader: "(step-6, finding <unknown>)"
 * becomes "(step-6)", "(finding <unknown>)" disappears.
 */
export type FindingRefSegment =
  | { kind: "text"; text: string }
  | { kind: "finding"; id: string; title: string };

const UUID =
  /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i;

export function splitFindingRefs(
  text: string,
  findings: ReadonlyMap<string, string>,
): FindingRefSegment[] {
  const segments: FindingRefSegment[] = [];
  let buffer = ""; // text since the last finding segment
  let rest = text;

  for (let match = UUID.exec(rest); match; match = UUID.exec(rest)) {
    const id = match[0].toLowerCase();
    const title = findings.get(id);
    let before = buffer + rest.slice(0, match.index);
    let after = rest.slice(match.index + match[0].length);

    if (title) {
      before = before.replace(/\bfinding\s+$/i, "");
      if (before) segments.push({ kind: "text", text: before });
      segments.push({ kind: "finding", id, title });
      buffer = "";
      rest = after;
      continue;
    }

    before = before.replace(/\bfindings?\s+$/i, "");
    if (/[,;]\s*$/.test(before)) {
      // "…step-6, finding X" → "…step-6"
      before = before.replace(/\s*[,;]\s*$/, "");
    } else if (/^\s*[,;]\s*/.test(after)) {
      // "(finding X, step-6)" → "(step-6)"
      after = after.replace(/^\s*[,;]\s*/, "");
    } else if (/\(\s*$/.test(before) && /^\s*\)/.test(after)) {
      // "was lost (finding X)." → "was lost."
      before = before.replace(/\s*\(\s*$/, "");
      after = after.replace(/^\s*\)/, "");
    } else if (/\s$/.test(before) && /^\s/.test(after)) {
      after = after.replace(/^\s+/, "");
    }
    buffer = before;
    rest = after;
  }

  const tail = buffer + rest;
  if (tail) segments.push({ kind: "text", text: tail });
  return segments;
}
